/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { omit, groupBy, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';
import BaseSelect from 'shared/ui/components/form-components/BaseSelect.vue';
import CreatePageModal from 'shared/ui/components/modals/CreatePageModal.vue';
import FormScheduler from './FormScheduler.vue';

// Constants
const RELATED_MODEL_GROUPS = ['content-page', 'map', 'person', 'questionnaire', 'session', 'lead'];

const __vue_script__ = {
    name: 'ContentPagePicker',

    components: {
        BaseSelect,
        FormScheduler
    },

    extends: BaseControl,

    props: {
        event: {
            type: Object,
            required: true
        },

        relatedModel: {
            type: String,
            default: null
        },

        createScript: {
            type: String,
            default: null
        },

        triggerSaveOnCreation: {
            type: Boolean,
            default: false
        },

        value: {
            type: [String, Array],
            default: null
        },

        options: {
            type: Object,
            default: () => ({})
        },

        mode: {
            type: String,
            default: null
        },

        edit: {
            type: Boolean,
            default: false
        },

        model: {
            type: Object,
            default: () => ({})
        },

        sentPropertyConfig: {
            type: Object,
            default: () => ({})
        },

        expirePropertyConfig: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        const attrs = omit(Object.assign(this.$options.propsData, this.$attrs), [
            'createScript',
            'edit',
            'editRoute',
            'event',
            'model',
            'relatedModel',
            'value',
            'options'
        ]);

        if (this.mode === 'schedulable-forms') {
            attrs.multiple = true;
        }

        return {
            attrs,
            formLink: null,
            ready: false,
            selectables: [],
            selectedPage: null,
        };
    },

    computed: {
        innerValue: {
            get() {
                return this.getValue(this.value);
            },

            set(value) {
                this.setValue(value);
            }
        },

        groupSelect() {
            return isNil(this.relatedModel);
        },

        editPageLink() {
            if (this.disableEdit) return null;

            const normalized = this.$utils.modules.normalizeRoute(this.options.editRoute);
            const id = this.$router.getRouteParams().modelId;
            return this.$utils.url.interpolateUrl(normalized, {
                eventId: this.event._id,
                pageId: this.innerValue?.value,
                modelId: id, _id: id
            });
        },

        multiple() {
            return this.mode === 'schedulable-forms';
        },

        pageChanged() {
            return this.statuses.changed;
        },

        disableEdit() {
            return !this.edit || !this.innerValue || this.pageChanged;
        },

        editTooltip() {
            return this.pageChanged ? this.$t('general.forms.content_page.page_not_saved') : null;
        }
    },

    created() {
        this.loadPages();
    },

    methods: {
        getValue(args) {
            return this.multiple ? this.findValues(args) : this.findValue(args);
        },

        setValue(arg) {
            let value = null;
            if (this.multiple) {
                value = [];
                for (const item of arg) {
                    if (typeof item === 'string') {
                        value.push(this.findValue(item));
                    } else if (typeof item === 'object') {
                        const itemPageId = item.pageId || item.value;
                        const existing = this.value.find(v => v.pageId === itemPageId);
                        if (existing) {
                            value.push(existing);
                        } else {
                            value.push({
                                _id: uuidv4(),
                                pageId: itemPageId,
                                name: item.label,
                                sentProperty: item.sentProperty || null,
                                expireProperty: item.expireProperty || null,
                                isInDetails: Boolean(item.isInDetails),
                            });
                        }
                    }
                }
            } else {
                value = arg?.value;
            }

            this.$emit('input', value || null);
        },

        findValues(pageIds) {
            const ids = Array.isArray(pageIds) ? pageIds : [];
            return ids.map(item => {
                if (typeof item === 'string') {
                    return this.findValue(item);
                }

                if (typeof item === 'object') {
                    return this.findValue(item.pageId);
                }
            }).filter(Boolean);

        },

        findValue(pageId) {
            if (this.groupSelect) {
                let value = null;
                for (const group of this.selectables) {
                    const page = group.pages.find(p => p?.value === pageId);
                    if (page) {
                        value = page;
                        break;
                    }
                }
                return value;
            }
            return this.selectables.find(o => o?.value === pageId);
        },

        async loadPages() {
            try {
                const pages = await this.$services.appScripts.callAppScript(this.event._id, 'fstg/get-model-pages-basic-info', {
                    relatedModel: this.relatedModel
                });

                this.selectables = this.groupSelect ?
                    this.splitPages(pages) :
                    Object.entries(pages).map(([id, label]) => ({ value: id, label }));

                this.ready = true;
            } catch (error) {
                console.error(`[ContentPagePicker] Could not load pages for model ${this.relatedModel}`, error);
            }
        },

        splitPages(pages) {
            // Sanitize names
            Object.entries(pages).forEach(([id, page]) => page.name = page.name || id);
            const groupedPages = groupBy(Object.values(pages), (p) => {
                if (!p.relatedModel || !RELATED_MODEL_GROUPS.includes(p.relatedModel)) return 'other';
                return p.relatedModel;
            });

            const options = [];
            for (const [group, groupPages] of Object.entries(groupedPages)) {
                options.push({
                    group,
                    type: this.$t(`general.forms.content_page.types.${group}`),
                    pages: groupPages.map((page) => ({ value: page._id, label: page.name || page._id }))
                });
            }

            options.sort((a, b) => {
                if (a.group === 'other') return 1;
                if (b.group === 'other') return -1;
                a.type.localeCompare(b.type);
            });

            return options;
        },

        getPageBehaviours(page, blocks) {
            let behaviours = [].concat(page.behaviours || []);

            Object.keys(page.blocks || {}).forEach(function(blockId) {
                const block = page.blocks[blockId];
                const blockDef = blocks[block.block];
                if (!blockDef) {
                    return;
                }

                behaviours = behaviours.concat(blockDef.behaviours || []);
            });

            return behaviours;
        },

        promptNewPage() {
            this.$modal.show(CreatePageModal, {
                event: this.event,
                relatedModel: this.relatedModel,
                onConfirm: (data) => this.createNewPage(data)
            }, {
                name: 'new-page-modal',
                classes: 'small',
                reset: true,
                height: 'auto'
            });
        },

        async createNewPage(data) {
            try {
                const { name } = data;
                if (!name) return;

                const page = this.options.pageDefaults || {};
                const createScript = this.createScript || 'fstg/create-empty-page';
                const { id } = await this.$services.appScripts.callAppScript(this.event._id, createScript, {
                    name, page,
                    relatedModel: this.relatedModel
                });

                await this.loadPages();
                this.innerValue = this.getValue(id);

                if (this.triggerSaveOnCreation) {
                    this.$emit('save');
                }

            } catch (error) {
                console.error(`[ContentPagePicker] Could not create new page for model ${this.relatedModel}`, error);
            }
        },

        configure(item) {
            this.formLink = this.value?.find(f => f.pageId === item?.value);
        },

        updateValue(value) {
            const values = [...this.value];
            for (const [index, link] of values.entries()) {
                if (link.pageId === value.pageId) {
                    values[index] = value;
                }
            }

            this.$emit('input', values);
            this.formLink = null;
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"content-page-picker form-group"},[(_vm.ready)?_c('base-select',_vm._b({attrs:{"track-by":"value","disabled":_vm.disabled,"errors":_vm.errors,"group-label":_vm.groupSelect ? 'type' : null,"group-values":_vm.groupSelect ? 'pages' : null,"group-select":_vm.groupSelect,"options":_vm.selectables},scopedSlots:_vm._u([{key:"control-actions",fn:function(ref){return [_c('div',{staticClass:"btn-group"},[(!_vm.multiple)?_c('a',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.editTooltip),expression:"editTooltip"}],staticClass:"btn btn-mini",attrs:{"href":_vm.editPageLink,"disabled":_vm.disableEdit}},[_vm._v(_vm._s(_vm.$t('general.edit')))]):_vm._e(),_c('a',{staticClass:"btn btn-mini has-action",attrs:{"href":"#"},on:{"click":_vm.promptNewPage}},[_vm._v(_vm._s(_vm.$t('general.create')))])])]}},{key:"tag",fn:function(ref){
var option = ref.option;
var search = ref.search;
var remove = ref.remove;
return [_c('span',{staticClass:"multiselect__tag"},[_c('span',[_vm._v(_vm._s(option && option.label))]),_c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.$t('agenda.forms.configure_form_link')),expression:"$t('agenda.forms.configure_form_link')"}],staticClass:"btn btn-mini btn-icon single",attrs:{"type":"button","aria-label":_vm.$t('agenda.forms.configure_form')},on:{"click":function($event){return _vm.configure(option)}}},[_c('i',{staticClass:"ic-gear"})]),_c('i',{staticClass:"multiselect__tag-icon",attrs:{"tabindex":"0"},on:{"click":function($event){return remove(option)}}})])]}}],null,false,3054202268),model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}},'base-select',_vm.attrs,false)):_vm._e(),(_vm.formLink)?_c('form-scheduler',{attrs:{"event":_vm.event,"sent-property-config":_vm.sentPropertyConfig,"expire-property-config":_vm.expirePropertyConfig},on:{"input":_vm.updateValue,"close":function($event){_vm.formLink = null}},model:{value:(_vm.formLink),callback:function ($$v) {_vm.formLink=$$v},expression:"formLink"}}):_vm._e()],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "ContentPagePicker.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__